import { useState } from "react";
import axios from "axios";
import { Ship, Train, Anchor, MapPin, Package as PkgIcon, User, Mail, Phone, Building2, CheckCircle2, Loader2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODES = [
  { key: "ocean_fcl", label: "Ocean · FCL", desc: "Full container", icon: Ship },
  { key: "ocean_lcl", label: "Ocean · LCL", desc: "Shared container", icon: Anchor },
  { key: "rail", label: "Rail", desc: "China ↔ Europe", icon: Train },
];

const CONTAINER_SIZES = [
  { key: "20ft", label: "20ft standard" },
  { key: "40ft", label: "40ft standard" },
  { key: "40ft_hc", label: "40ft high cube" },
];

const INCOTERMS = ["EXW", "FOB", "CIF", "CFR", "DAP", "DDP"];

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-[11px] font-semibold text-[var(--tt-muted)] uppercase tracking-widest mb-1.5">
      {label} {required && <span className="text-[var(--tt-accent)]">*</span>}
    </span>
    {children}
  </label>
);

const Input = (props) => (
  <input
    {...props}
    className="w-full rounded-xl border border-[var(--tt-line)] bg-white px-4 py-2.5 text-sm outline-none focus:border-[var(--tt-green)] transition"
  />
);

const Select = (props) => (
  <select
    {...props}
    className="w-full rounded-xl border border-[var(--tt-line)] bg-white px-4 py-2.5 text-sm outline-none focus:border-[var(--tt-green)] transition"
  />
);

const FreightQuoteForm = ({ defaultOrigin = "", defaultDestination = "", defaultOriginPort = "", defaultDestinationPort = "", defaultMode = "ocean_fcl", compact = false }) => {
  const [mode, setMode] = useState(defaultMode);
  const [origin, setOrigin] = useState(defaultOrigin);
  const [destination, setDestination] = useState(defaultDestination);
  const [originPort, setOriginPort] = useState(defaultOriginPort);
  const [destinationPort, setDestinationPort] = useState(defaultDestinationPort);
  const [containerSize, setContainerSize] = useState("40ft");
  const [containerCount, setContainerCount] = useState(1);
  const [cbm, setCbm] = useState("");
  const [weight, setWeight] = useState("");
  const [commodity, setCommodity] = useState("");
  const [incoterms, setIncoterms] = useState("FOB");
  const [readyDate, setReadyDate] = useState("");
  const [hazardous, setHazardous] = useState(false);

  const [company, setCompany] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (!contactName || !email || !origin || !destination) {
        throw new Error("Please fill in origin, destination, name, and email.");
      }
      await axios.post(`${API}/freight/quote`, {
        mode,
        origin,
        destination,
        origin_port: originPort || null,
        destination_port: destinationPort || null,
        container_size: mode === "ocean_fcl" ? containerSize : null,
        container_count: mode === "ocean_fcl" ? Number(containerCount) || 1 : null,
        cbm: cbm ? Number(cbm) : null,
        weight_kg: weight ? Number(weight) : null,
        commodity: commodity || null,
        incoterms: incoterms || null,
        cargo_ready_date: readyDate || null,
        hazardous,
        company: company || null,
        contact_name: contactName,
        email,
        phone: phone || null,
        notes: notes || null,
      });
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || "Something went wrong");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="rounded-3xl bg-gradient-to-br from-[var(--tt-mint-soft)] to-white border border-[var(--tt-line)] p-8 text-center" data-testid="freight-quote-success">
        <CheckCircle2 className="h-14 w-14 text-[var(--tt-green)] mx-auto mb-3" />
        <h3 className="font-display font-black text-2xl text-[var(--tt-navy)]">Request received</h3>
        <p className="text-sm text-[var(--tt-muted)] mt-2 max-w-md mx-auto">
          Thanks {contactName.split(" ")[0]} — our freight desk will get back to you within one business day
          with a rate and space availability for {origin} → {destination}.
        </p>
        <a href="/services" className="mt-6 inline-block text-sm font-semibold text-[var(--tt-green)] hover:text-[var(--tt-navy)]">← Back to services</a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={`rounded-3xl border border-[var(--tt-line)] bg-white ${compact ? "p-6" : "p-6 md:p-8"} shadow-sm`} data-testid="freight-quote-form">
      <div className="mb-5">
        <div className="text-[11px] font-semibold text-[var(--tt-green)] uppercase tracking-widest">Freight quote</div>
        <h3 className="font-display font-black text-2xl md:text-3xl text-[var(--tt-navy)] tracking-tight mt-1">
          Tell us about your shipment
        </h3>
        <p className="text-sm text-[var(--tt-muted)] mt-1">Ocean & rail. We reply within one business day.</p>
      </div>

      {/* Mode selector */}
      <div className="grid grid-cols-3 gap-2 mb-6" data-testid="freight-mode-picker">
        {MODES.map((m) => {
          const active = mode === m.key;
          const Icon = m.icon;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => setMode(m.key)}
              data-testid={`freight-mode-${m.key}`}
              className={`rounded-2xl border p-3 text-left transition ${
                active
                  ? "border-[var(--tt-green)] bg-[var(--tt-mint-soft)] shadow-sm"
                  : "border-[var(--tt-line)] bg-white hover:border-[var(--tt-green)]"
              }`}
            >
              <Icon className={`h-5 w-5 mb-1.5 ${active ? "text-[var(--tt-green)]" : "text-[var(--tt-navy)]"}`} />
              <div className="text-sm font-bold text-[var(--tt-navy)]">{m.label}</div>
              <div className="text-[10px] text-[var(--tt-muted)] mt-0.5">{m.desc}</div>
            </button>
          );
        })}
      </div>

      {/* Route */}
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <Field label="Origin country / city" required>
          <Input value={origin} onChange={(e) => setOrigin(e.target.value)} placeholder="China" data-testid="freight-origin" required />
        </Field>
        <Field label="Destination country / city" required>
          <Input value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="Zambia" data-testid="freight-destination" required />
        </Field>
        <Field label={mode === "rail" ? "Origin terminal" : "Origin port"}>
          <Input value={originPort} onChange={(e) => setOriginPort(e.target.value)} placeholder={mode === "rail" ? "Xi'an" : "Shanghai (CNSHA)"} data-testid="freight-origin-port" />
        </Field>
        <Field label={mode === "rail" ? "Destination terminal" : "Destination port"}>
          <Input value={destinationPort} onChange={(e) => setDestinationPort(e.target.value)} placeholder={mode === "rail" ? "Duisburg" : "Dar es Salaam (TZDAR)"} data-testid="freight-destination-port" />
        </Field>
      </div>

      {/* Cargo */}
      {mode === "ocean_fcl" ? (
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <Field label="Container size">
            <Select value={containerSize} onChange={(e) => setContainerSize(e.target.value)} data-testid="freight-container-size">
              {CONTAINER_SIZES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </Select>
          </Field>
          <Field label="Number of containers">
            <Input type="number" min="1" value={containerCount} onChange={(e) => setContainerCount(e.target.value)} data-testid="freight-container-count" />
          </Field>
          <Field label="Total weight (kg, optional)">
            <Input type="number" min="0" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="e.g. 12000" data-testid="freight-weight" />
          </Field>
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <Field label="Volume (CBM)" required>
            <Input type="number" step="0.1" min="0" value={cbm} onChange={(e) => setCbm(e.target.value)} placeholder="e.g. 8.5" data-testid="freight-cbm" required={mode !== "ocean_fcl"} />
          </Field>
          <Field label="Weight (kg)">
            <Input type="number" min="0" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="e.g. 1500" data-testid="freight-weight" />
          </Field>
          <Field label="Cargo ready date">
            <Input type="date" value={readyDate} onChange={(e) => setReadyDate(e.target.value)} data-testid="freight-ready-date" />
          </Field>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4 mb-4">
        <Field label="Commodity">
          <Input value={commodity} onChange={(e) => setCommodity(e.target.value)} placeholder="Auto parts, textiles…" data-testid="freight-commodity" />
        </Field>
        <Field label="Incoterms">
          <Select value={incoterms} onChange={(e) => setIncoterms(e.target.value)} data-testid="freight-incoterms">
            {INCOTERMS.map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
        </Field>
        {mode === "ocean_fcl" && (
          <Field label="Cargo ready date">
            <Input type="date" value={readyDate} onChange={(e) => setReadyDate(e.target.value)} data-testid="freight-ready-date" />
          </Field>
        )}
        <label className="flex items-center gap-2 text-sm md:col-span-3 mt-1 cursor-pointer">
          <input type="checkbox" checked={hazardous} onChange={(e) => setHazardous(e.target.checked)} data-testid="freight-hazardous" className="rounded border-[var(--tt-line)] text-[var(--tt-green)] focus:ring-[var(--tt-green)]" />
          <span className="text-[var(--tt-ink-soft)]">Cargo is hazardous / requires DG documentation</span>
        </label>
      </div>

      <div className="border-t border-[var(--tt-line)] my-6" />

      {/* Contact */}
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <Field label="Full name" required>
          <Input value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Alex Kayanga" data-testid="freight-name" required />
        </Field>
        <Field label="Company (optional)">
          <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Acme Co." data-testid="freight-company" />
        </Field>
        <Field label="Email" required>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="freight-email" required />
        </Field>
        <Field label="Phone">
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 …" data-testid="freight-phone" />
        </Field>
      </div>

      <Field label="Notes (optional)">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Any special handling, deadline, or preferred carrier?"
          data-testid="freight-notes"
          className="w-full rounded-xl border border-[var(--tt-line)] bg-white px-4 py-2.5 text-sm outline-none focus:border-[var(--tt-green)] transition resize-none"
        />
      </Field>

      {err && (
        <div className="mt-4 flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3" data-testid="freight-error">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {err}
        </div>
      )}

      <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-[var(--tt-muted)]">We reply within 1 business day · No account needed</div>
        <button
          type="submit"
          disabled={busy}
          data-testid="freight-submit"
          className="inline-flex items-center gap-2 rounded-full bg-[var(--tt-green)] text-white font-bold px-7 py-3 text-sm shadow-lg hover:bg-[var(--tt-navy)] transition disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {busy ? "Sending…" : "Request quote"}
        </button>
      </div>
    </form>
  );
};

export default FreightQuoteForm;
